import type { FC, ReactNode } from 'react'
import type { Design } from '@/lib/design/types'

import Image from 'next/image'
import { Clapperboard, Grid3x3, Layers, SquareUser } from 'lucide-react'
import { SlidePreview } from '@/components/shared/slide-preview'

interface PhoneMockProps {
    planned: Array<Design>
    photos: Array<string>
}

// A static picture of the feed simulator for the home page.
export const PhoneMock: FC<PhoneMockProps> = ({ planned, photos }): ReactNode => {
    const tiles: Array<{ kind: 'planned', design: Design } | { kind: 'photo', src: string }> = [
        ...planned.map(design => ({ kind: 'planned' as const, design })),
        ...photos.map(src => ({ kind: 'photo' as const, src }))
    ]

    return (
        <div className='mx-auto w-full max-w-[340px] rounded-[44px] border-[10px] border-ink bg-ink shadow-2xl' role='img' aria-label='A phone showing an Instagram profile grid with three planned carousel posts at the top and six existing photos below'>
            <div className='overflow-hidden rounded-[34px] bg-white text-neutral-900'>
                <div className='flex h-8 items-center justify-between px-6 text-[11px] font-semibold' aria-hidden='true'>
                    <span>9:41</span>
                    <span className='h-4 w-20 rounded-full bg-ink' />
                    <span className='w-6' />
                </div>

                <div className='flex flex-col gap-3 px-4 pt-2' aria-hidden='true'>
                    <p className='text-center text-sm font-semibold'>slowgrowth</p>
                    <div className='flex items-center gap-4'>
                        <div className='shrink-0 rounded-full bg-gradient-to-tr from-[#F9CE34] via-[#EE2A7B] to-[#6228D7] p-[2px]'>
                            <div className='flex size-16 items-center justify-center rounded-full border-2 border-white bg-ink font-heading text-xl font-bold text-coral'>
                                SG
                            </div>
                        </div>
                        <div className='flex flex-1 justify-around text-center text-xs leading-tight'>
                            <span><b className='block text-sm'>214</b>posts</span>
                            <span><b className='block text-sm'>18.2K</b>followers</span>
                            <span><b className='block text-sm'>402</b>following</span>
                        </div>
                    </div>
                    <div className='text-xs leading-snug'>
                        <p className='font-semibold'>Slow Growth</p>
                        <p>Honest notes on growing a small business.</p>
                        <p className='font-semibold text-[#00376B]'>slowgrowth.blog</p>
                    </div>
                    <div className='-mx-4 grid grid-cols-3 border-t border-neutral-200'>
                        <span className='flex justify-center border-b-2 border-neutral-900 py-2'><Grid3x3 className='size-5' /></span>
                        <span className='flex justify-center py-2 text-neutral-400'><Clapperboard className='size-5' /></span>
                        <span className='flex justify-center py-2 text-neutral-400'><SquareUser className='size-5' /></span>
                    </div>
                </div>

                <div className='grid grid-cols-3 gap-[2px]' aria-hidden='true'>
                    {tiles.map((tile, index) => (
                        <div key={index} className='relative aspect-[3/4] overflow-hidden bg-neutral-200'>
                            {tile.kind === 'planned' ? (
                                <>
                                    <SlidePreview design={tile.design} index={0} width={106} fluid />
                                    <Layers className='absolute top-1 right-1 size-3.5 text-white drop-shadow' />
                                    <span className='absolute bottom-1 left-1 rounded-full bg-primary px-1.5 py-px text-[8px] font-bold tracking-wide text-primary-foreground uppercase'>Planned</span>
                                </>
                            ) : (
                                <Image src={tile.src} alt='' fill sizes='120px' className='object-cover' />
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}
